import { isFloatingRateLoanProductFields } from '@src/types/OTC/otcGuards';
import type {
  FloatingRateLoanDealProductBuilderRow,
  OTCFloatingRateLoanFieldsData,
} from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import {
  DayCountConventionEnum,
  EMPTY_OBJECT,
  LoanQuoteTerminationTypeEnum,
  PrepaymentOptionEnum,
  runValidationV1,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { boolean, object, string, type InferType } from 'yup-v1-latest';
import { LOAN_PAYMENT_PERIOD_OPTIONS } from '../OTCFieldsDialog/productLayoutTokens';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { isDurationLessThanOrEqual, isDurationWithinLoanOtcMax } from './durationComparisonUtils';
import { positiveNumericString } from './validationUtils';

export const FloatingRateLoanProductBuilderSliceStrategy: BaseProductBuilderSliceStrategy<FloatingRateLoanDealProductBuilderRow> =
  {
    getDefaultValues: (
      existingOTCFieldsForClear?: FloatingRateLoanDealProductBuilderRow['otcFields']
    ): NonNullable<FloatingRateLoanDealProductBuilderRow['otcFields']> => {
      const loanDefaults: OTCFloatingRateLoanFieldsData = {
        productType: 'floatingRateLoan',
        unitLoanAmount: '1',
        term: null,
        termType: LoanQuoteTerminationTypeEnum.Fixed,
        initialTerm: null,
        paymentPeriod: '1M',
        accrualPeriod: '1M',
        resetPeriod: '1D',
        dayCountConvention: DayCountConventionEnum.Thirty360,
        prepaymentOption: PrepaymentOptionEnum.None,
        callOption: false,
      };

      if (isFloatingRateLoanProductFields(existingOTCFieldsForClear)) {
        return { ...loanDefaults, ...existingOTCFieldsForClear };
      }

      return loanDefaults;
    },

    buildNoSuffixInstrumentName: (updatedRow: WritableDraft<FloatingRateLoanDealProductBuilderRow>): string => {
      if (updatedRow && updatedRow.baseAsset) {
        let instrument = `LOAN-${updatedRow.baseAsset}`;
        if (updatedRow.collateralAsset) {
          instrument += `-${updatedRow.collateralAsset}`;
        }
        if (updatedRow.otcFields?.term) {
          instrument += `-${updatedRow.otcFields.term}`;
        }
        instrument += '-FLOATING';
        return instrument;
      }
      return '';
    },

    updateGridRowData: (
      state: WritableDraft<FloatingRateLoanDealProductBuilderRow>,
      rowData: FloatingRateLoanDealProductBuilderRow,
      fixingIndexMapping?: FixingIndexMappingMap
    ): void => {
      state.baseAsset = rowData.baseAsset;
      state.collateralAsset = rowData.collateralAsset;
      state.productTemplate = rowData.productTemplate;
      state.uniqueSuffix = rowData.uniqueSuffix;
    },

    updateOTCFields: (
      state: WritableDraft<FloatingRateLoanDealProductBuilderRow>,
      otcFields: FloatingRateLoanDealProductBuilderRow['otcFields'],
      fixingIndexMapping?: FixingIndexMappingMap
    ): void => {
      // Shallow-copy non-null payloads so we never persist frozen action references in draft state.
      state.otcFields = otcFields == null ? otcFields : { ...otcFields };
    },

    validateFormState: (
      currentRow: FloatingRateLoanDealProductBuilderRow,
      fixingIndexMapping?: FixingIndexMappingMap
    ): ReturnType<typeof runValidationV1> => {
      const schema = fixingIndexMapping
        ? createFloatingRateLoanProductBuilderSchema(fixingIndexMapping)
        : floatingRateLoanProductBuilderSchema;
      return runValidationV1(schema, currentRow);
    },

    validateOTCFields: (
      otcFields: FloatingRateLoanDealProductBuilderRow['otcFields'],
      fixingIndexMapping?: FixingIndexMappingMap
    ): ReturnType<typeof runValidationV1> => {
      return runValidationV1(floatingRateLoanOtcFieldsSchema, otcFields ?? EMPTY_OBJECT);
    },
  };

/** UI-side validation schema for floating rate loan OTC Fields form */
export const floatingRateLoanOtcFieldsSchema = object().shape({
  productType: string()
    .oneOf(['floatingRateLoan'] as const)
    .required('Product type is required'),
  unitLoanAmount: positiveNumericString('Unit loan amount'),
  termType: string()
    .oneOf([LoanQuoteTerminationTypeEnum.Fixed, LoanQuoteTerminationTypeEnum.OpenTerm] as const)
    .required('Term type is required'),
  term: string().when('termType', {
    is: LoanQuoteTerminationTypeEnum.Fixed,
    then: schema =>
      schema
        .required('Term is required when Term Type is Fixed')
        .test('term-max', 'Term must be at most 1 year', value => {
          if (!value) {
            return true;
          }
          return isDurationWithinLoanOtcMax(value);
        }),
    otherwise: schema => schema.nullable().oneOf([null], 'Term must be empty when Term Type is Open'),
  }),
  initialTerm: string().when('termType', {
    is: LoanQuoteTerminationTypeEnum.OpenTerm,
    then: schema =>
      schema
        .nullable()
        .optional()
        .test('initial-term-max', 'Initial term must be at most 1 year', value => {
          if (value == null || value === '') {
            return true;
          }
          return isDurationWithinLoanOtcMax(value);
        }),
    otherwise: schema => schema.nullable().oneOf([null], 'Initial Term must be empty when Term Type is Fixed'),
  }),
  loanPaymentCcy: string().required('Loan Payment Currency is required'),
  paymentPeriod: string()
    .oneOf(
      LOAN_PAYMENT_PERIOD_OPTIONS.map(item => item.value),
      'Invalid Payment period symbol'
    )
    .required('Payment period is required'),
  accrualPeriod: string()
    .oneOf(
      LOAN_PAYMENT_PERIOD_OPTIONS.map(item => item.value),
      'Invalid Accrual period symbol'
    )
    .required('Accrual period is required'),
  dayCountConvention: string()
    .oneOf(Object.values(DayCountConventionEnum), 'Invalid Day Count Convention')
    .required('Day Count Convention is required'),
  rateSource: string().required('Rate Source is required'),
  resetPeriod: string()
    .oneOf(
      LOAN_PAYMENT_PERIOD_OPTIONS.map(item => item.value),
      'Invalid Reset period symbol'
    )
    .required('Reset period is required')
    .test('reset-period-length', 'Reset period length must not be longer than term', function (value) {
      const { termType, term, initialTerm } = this.parent;

      if (!value) {
        return true; // Let required() handle empty values
      }

      // Determine which term to compare against
      const termToCompare = termType === LoanQuoteTerminationTypeEnum.Fixed ? term : initialTerm;

      if (!termToCompare) {
        return true; // If no term is set, skip this validation
      }

      const isValid = isDurationLessThanOrEqual(value, termToCompare);

      if (isValid === null) {
        return true; // If comparison fails due to invalid format, skip validation
      }

      return isValid;
    }),
  prepaymentOption: string()
    .oneOf([PrepaymentOptionEnum.None, PrepaymentOptionEnum.Full, PrepaymentOptionEnum.Partial] as const)
    .required('Prepayment Option is required'),
  callOption: boolean().required('Call Option is required'),
});

export const createFloatingRateLoanProductBuilderSchema = (fixingIndexMapping?: FixingIndexMappingMap) =>
  object({
    productTemplate: string().required('Product Template is required'),
    product: string()
      .oneOf(['floatingRateLoan'] as const)
      .required('Product is required'),
    baseAsset: string()
      .required('Borrowed Asset is required')
      .test('asset-mismatch', 'Borrowed Asset and Collateral Asset cannot be the same', function (value) {
        const { collateralAsset } = this.parent;
        if (!value || !collateralAsset) {
          return true;
        }
        return value !== collateralAsset;
      }),
    collateralAsset: string()
      .required('Collateral Asset is required')
      .test('asset-mismatch', 'Borrowed Asset and Collateral Asset cannot be the same', function (value) {
        const { baseAsset } = this.parent;
        if (!value || !baseAsset) {
          return true;
        }
        return value !== baseAsset;
      }),
    uniqueSuffix: string().nullable(),
    otcFields: floatingRateLoanOtcFieldsSchema.required('OTC fields are required'),
  }).test(
    'loan-pair-has-fixing',
    'Borrowed Asset and Collateral Asset pair must have at least one fixing',
    function (row) {
      if (!row.baseAsset || !row.collateralAsset) {
        return true;
      }

      const normalizedCollateralAsset =
        row.collateralAsset === 'USDT' || row.collateralAsset === 'USDC' ? 'USD' : row.collateralAsset;
      const normalizedBorrowedAsset = row.baseAsset === 'USDT' || row.baseAsset === 'USDC' ? 'USD' : row.baseAsset;

      if (fixingIndexMapping && fixingIndexMapping.size > 0) {
        const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
          normalizedCollateralAsset,
          normalizedBorrowedAsset,
          fixingIndexMapping
        );
        const availableFixings = fixings.filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name);
        return availableFixings.length > 0;
      }

      return true;
    }
  );

export const floatingRateLoanProductBuilderSchema = createFloatingRateLoanProductBuilderSchema();

export type ValidatedFloatingRateLoanRowData = InferType<typeof floatingRateLoanProductBuilderSchema>;
